import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AuthState, AuthUser, ContextCompany, LoginResponse } from '@/types/auth.types';

// Changed based on Dinisuru's request: Auth tokens and user profiles are stored exclusively in
// browser memory (Redux state) and never persisted to localStorage to prevent XSS exfiltration.
// The refreshToken is stored in an HttpOnly cookie and used for silent refresh on page reload / new tab.

const initialState: AuthState = {
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  accessibleLocations: [],
  accessibleCostCenters: [],
  accessibleCompanies: [],
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart(state) {
      state.isLoading = true;
    },
    loginSuccess(state, action: PayloadAction<LoginResponse>) {
      // Did the company change under us? Compare BEFORE overwriting state.user.
      const companyChanged =
        !!state.user && state.user.companyId !== action.payload.user.companyId;

      state.user = action.payload.user;
      state.accessToken = action.payload.accessToken;
      state.isAuthenticated = true;
      state.isLoading = false;
      state.isInitialized = true;

      // Preserve accessible lists across a context switch, where select-context may not resend
      // them. But NEVER across a COMPANY switch (ENH-001): those lists belong to the company we
      // just left. If the company we entered has no locations the payload is legitimately empty,
      // and preserving the old ones would leave the previous company's sites on screen — and,
      // worse, selectable on the next posting.
      if (companyChanged) {
        state.accessibleLocations   = action.payload.accessibleLocations   ?? [];
        state.accessibleCostCenters = action.payload.accessibleCostCenters ?? [];
      } else {
        if (action.payload.accessibleLocations?.length) {
          state.accessibleLocations = action.payload.accessibleLocations;
        }
        if (action.payload.accessibleCostCenters?.length) {
          state.accessibleCostCenters = action.payload.accessibleCostCenters;
        }
      }

      // The company list itself is a property of the USER, not the company, so it survives a
      // switch and is only replaced when the server sends a fresh one.
      if (action.payload.accessibleCompanies?.length) {
        state.accessibleCompanies = action.payload.accessibleCompanies;
      }
    },
    loginFailure(state) {
      state.isLoading = false;
      state.isInitialized = true;
    },
    tokenRefreshed(state, action: PayloadAction<{ accessToken: string }>) {
      state.accessToken = action.payload.accessToken;
    },
    logout(state) {
      state.user = null;
      state.accessToken = null;
      state.isAuthenticated = false;
      state.isLoading = false;
      state.isInitialized = true;
      state.accessibleLocations = [];
      state.accessibleCostCenters = [];
      state.accessibleCompanies = [];
    },
    setInitialized(state) {
      state.isInitialized = true;
      state.isLoading = false;
    },
    setUser(state, action: PayloadAction<AuthUser>) {
      state.user = action.payload;
    },
    clearForcePasswordChange(state) {
      if (state.user) {
        state.user = { ...state.user, forcePasswordChange: false };
      }
    },
    /** Refreshes just the accessible-company list without touching the rest of auth state.
     *  Used by the context-switcher modal so it shows current grants even when Redux is stale. */
    setAccessibleCompanies(state, action: PayloadAction<ContextCompany[]>) {
      state.accessibleCompanies = action.payload;
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  tokenRefreshed,
  logout,
  setInitialized,
  setUser,
  clearForcePasswordChange,
  setAccessibleCompanies,
} = authSlice.actions;

export default authSlice.reducer;
